// @flow

import React from 'react';
import {connect} from 'react-redux';

import {CurrentUserSelector} from 'app/current-user/api';
import {gt} from 'app/utils/i18n';
import {getContentType} from 'app/utils/content-type';
import {TEAMS_CREATE_PAGE_URL} from 'app/urls';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {
  runWhenAllRelatedMatch,
  type ModelChangeMessageType,
} from 'app/subscriptions';

import GenericAttachedItemsSidebar from 'app/components/general/GenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {type ModelSubscribedComponentPropsType} from 'app/subscriptions';
import {fetchTeams} from 'app/teams/actions';
import {requestTeams} from 'app/teams/fetchers';
import type {
  TeamsLocationParamsType,
  TeamType,
  BasicTeamType,
  TeamMembershipType,
} from 'app/teams/types';
import {get} from 'app/utils/request';
import {SIDEBAR_PAGE_SIZE, UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import type {StoreShapeType} from 'app/reducers/types';
import type {Props as ItemProps} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import {getCurrentUserPerms} from 'app/current-user/utils';

type OwnProps = {|
  objectId: number,
  objectContentType: 'procedure' | 'proceduregroup' | 'policy',
  objectURL: string,
  objectTeams: Array<BasicTeamType>,
  addToTeam: (BasicTeamType, Array<{url: string}>) => Promise<any>,
  removeFromTeam: (
    BasicTeamType,
    {team_memberships: Array<TeamMembershipType>}
  ) => Promise<any>,
  canEditObject: boolean,
  fetchObject: () => Promise<any>,
  objectTeamsMembership: Array<TeamMembershipType>,
  itemComponentProps?:
    | $Shape<ItemProps>
    | ((TeamType, boolean) => $Shape<ItemProps>),
  onClose: () => void,
|};

type ModelSubscribedOwnProps = {|
  ...OwnProps,
  ...ModelSubscribedComponentPropsType,
|};

type StateProps = {|
  isManager: boolean,
  isSuperManager: boolean,
  items: Array<TeamType>,
|};

type DispatchProps = {|
  requestTeams: (any) => Promise<any>,
  fetchTeams: (locationParams: TeamsLocationParamsType) => Promise<any>,
|};

type Props = {|
  ...ModelSubscribedOwnProps,
  ...StateProps,
  ...DispatchProps,
|};

class BaseDetailPageGenericTeamsSidebar extends React.Component<Props> {
  joinTeamIds = (teams: Array<BasicTeamType>) =>
    teams.map(({id}) => id).join(',');

  loadItems = () => {
    const {fetchTeams, objectTeams} = this.props;

    return Promise.resolve(
      objectTeams.length > 0
        ? fetchTeams({
            id: this.joinTeamIds(objectTeams),
            page_size: UNLIMITED_PAGE_SIZE,
          })
        : null
    );
  };

  componentDidMount() {
    this.props.fetchObject();
  }

  componentDidUpdate({objectTeams}: Props) {
    if (
      this.joinTeamIds(objectTeams) !== this.joinTeamIds(this.props.objectTeams)
    ) {
      this.loadItems();
    }
  }

  render() {
    const {
      objectURL,
      objectId,
      canEditObject,
      // eslint-disable-next-line no-unused-vars
      fetchTeams,
      // eslint-disable-next-line no-unused-vars
      objectTeams,
      addToTeam,
      isManager,
      removeFromTeam,
      fetchObject,
      objectTeamsMembership,
      isSuperManager,
      objectContentType,
      ...other
    } = this.props;
    // eslint-enable no-unused-vars

    return (
      <GenericAttachedItemsSidebar
        //  show/edit items
        loadItems={this.loadItems}
        itemNameProcessor={(team) => team.name}
        title={gt('Teams')}
        handleModelChange={(message: ModelChangeMessageType) => {
          runWhenAllRelatedMatch(
            message,
            'team',
            {
              content_type: objectContentType,
              id: parseInt(objectId),
            },
            this.loadItems
          );
        }}
        onRemove={(team) =>
          removeFromTeam(team, {team_memberships: objectTeamsMembership}).then(
            fetchObject
          )
        }
        // attach
        showFilterField={canEditObject}
        searchPlaceholder={gt('Search teams...')}
        onSearch={(search) =>
          requestTeams({search, page_size: SIDEBAR_PAGE_SIZE})
        }
        onLoadMore={(response) => get(response.next)}
        onAttach={(team) =>
          addToTeam(team, [{url: objectURL}]).then(fetchObject)
        }
        // create
        canCreate={isSuperManager}
        createLabel={gt('Create team')}
        createURL={TEAMS_CREATE_PAGE_URL}
        emptyResultsComponents={
          <NoResultsMessage
            onSidebar={true}
            icon="fa-users"
            head={gt('No Teams')}
          >
            {isManager && canEditObject
              ? gt(
                  `You can add this ${
                    getContentType(objectContentType).name
                  } to existing teams, remove it from a team or create a new team using the button below`
                )
              : null}
          </NoResultsMessage>
        }
        {...other}
      />
    );
  }
}

const mapStateToProps = (state, {objectTeams}): StateProps => {
  const allTeams = state.teamsArea.getIn(['teams', 'byId']);

  const {isManager, isSuperManager} = getCurrentUserPerms(CurrentUserSelector(state).data);
  return {
    isManager,
    isSuperManager,
    items: objectTeams
      .map((team) => {
        // $FlowFixMe
        const iTeam = allTeams.get(team.id.toString());
        // $FlowFixMe
        return iTeam != null ? iTeam.toJS() : null;
      })
      .filter((t) => t),
  };
};

export const DetailPageGenericTeamsSidebar = keepUpdatedEnhancer<OwnProps>(
  connect<
    Props,
    ModelSubscribedOwnProps,
    StateProps,
    DispatchProps,
    StoreShapeType,
    _
  >(mapStateToProps, {
    // $FlowFixMe
    fetchTeams,
    requestTeams,
  })(BaseDetailPageGenericTeamsSidebar)
);
